/**
 * fiko-makro.marangoz.ts — Marangozluk + Mobilya makroları (saf üreteçler).
 *
 * fiko-makro.insaat.ts ile AYNI desen: her makro mevcut motorun anladığı IOzellik[]
 * dizisi üretir (primitif kutu/silindir, ekleme=true birleşim / false delik-çıkarma).
 * Yeni motor YOK; FikoModellemeYonetici.rebuild() bunları CSG ile kurar, tek finalize.
 *
 * Konvansiyon (insaat ile birebir):
 *   - kutuFeat(en=X, boy=Y, yuk=Z, duzlem) → taban orta-noktası düzlem origin'inde, +Z'ye büyür.
 *   - silindirFeat(cap, yuk, duzlem) → taban dairesi origin'de, +Z'ye büyür.
 *   - xyDuzlem(ox,oy,oz) → XY tabanına paralel, dünya origin'i (ox,oy,oz).
 * Dolayısıyla bir parçayı konumlamak = origin'i parçanın TABAN-ORTA noktasına koymak.
 *
 * Mobilya yerel ekseni: X=en(genişlik), Y=boy(derinlik), Z=yükseklik(yukarı +).
 *
 * Marangoz terimleri:
 *   Karkas/Gövde=kutu iskeleti · Kiniş(dado)=dik oluk · Lamba(rabbet)=kenar basamağı
 *   Kavela(dowel)=silindir delik · Raf · Kapak(çıta+ayna) · Çekmece
 */

import { ISketchDuzlem } from './FikoSketch';
import { IOzellik } from './FikoModellemeYonetici';
import { xyDuzlem, kutuFeat, silindirFeat, IMakroParam } from './fiko-makro.insaat';

/** Kalınlık merkezli kutu: merkez (cx,cy) düzlemde, taban kotu cz. sx=en, sy=boy, sz=yük. */
function kutuMerkez(sx: number, sy: number, sz: number, cx: number, cy: number, cz: number, ekleme = true): IOzellik {
  return kutuFeat(sx, sy, sz, xyDuzlem(cx, cy, cz), ekleme);
}

/**
 * PANEL — tek ahşap levha (en × boy yüzeyli, kalınlık kadar yüksek).
 * Origin = parçanın bir köşesi değil, taban-orta; panel (0..en, 0..boy) bölgesine oturur.
 */
export function panelMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 600, boy = +p['boy'] || 400, t = +p['kalinlik'] || 18;
  return [kutuMerkez(en, boy, t, en / 2, boy / 2, 0, true)];
}

/**
 * KARKAS — dolap gövdesi: 2 yan + alt + üst panel, opsiyonel sırt.
 * Yanlar dışta (tam yük/derinlik), alt-üst yanlar arasında (iç genişlik), sırt arkada (ince).
 */
export function karkasMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 600, boy = +p['boy'] || 350, yuk = +p['yuk'] || 720;
  const t = +p['kalinlik'] || 18;
  const st = +p['sirtKalinlik'] || 6;
  const sirt = p['sirtVar'] !== false && p['sirtVar'] !== 'false';
  const icEn = en - 2 * t;

  const out: IOzellik[] = [
    kutuMerkez(t, boy, yuk, t / 2, boy / 2, 0, true),               // sol yan
    kutuMerkez(t, boy, yuk, en - t / 2, boy / 2, 0, true),          // sağ yan
    kutuMerkez(icEn, boy, t, en / 2, boy / 2, 0, true),            // alt
    kutuMerkez(icEn, boy, t, en / 2, boy / 2, yuk - t, true),      // üst
  ];
  if (sirt) out.push(kutuMerkez(icEn, st, yuk - 2 * t, en / 2, st / 2, t, true)); // sırt
  return out;
}

/**
 * RAFLI DOLAP — karkas + N eşit aralıklı ara raf (iç genişlikte, sırttan boşluklu).
 */
export function rafliDolapMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 600, boy = +p['boy'] || 350, yuk = +p['yuk'] || 720;
  const t = +p['kalinlik'] || 18;
  const st = +p['sirtKalinlik'] || 6;
  const sirt = p['sirtVar'] !== false && p['sirtVar'] !== 'false';
  const rafN = Math.max(0, Math.round(+p['rafSayisi'] || 2));

  const out = karkasMakro(p);
  const icEn = en - 2 * t;
  const icYuk = yuk - 2 * t;
  const rafDerin = boy - (sirt ? st : 0) - 5;     // sırttan 5mm boşluk
  const bolme = rafN + 1;
  for (let i = 1; i <= rafN; i++) {
    const z = t + (icYuk * i) / bolme - t / 2;     // raf taban kotu (rafın altı)
    out.push(kutuMerkez(icEn, rafDerin, t, en / 2, rafDerin / 2, z, true));
  }
  return out;
}

/**
 * KİNİŞLİ PANO (dado) — pano + üst yüzeye, boy ekseni boyunca açılmış dik oluk.
 * Oluk X=konum (varsayılan ortada), genislik kadar geniş, derinlik kadar derin.
 */
export function kinisliPanoMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 600, boy = +p['boy'] || 400, t = +p['kalinlik'] || 18;
  const ow = +p['genislik'] || 18;                      // oluk genişliği (oturacak panel kalınlığı)
  const od = Math.min(t - 1, +p['derinlik'] || t / 2);  // oluk derinliği
  const ox = (typeof p['konum'] === 'number') ? +p['konum'] : en / 2;
  return [
    kutuMerkez(en, boy, t, en / 2, boy / 2, 0, true),
    // oluk: üstten od kadar oyulur (taban kotu t-od), boy boyunca +2 taşar → temiz boolean
    kutuMerkez(ow, boy + 2, od + 0.5, ox, boy / 2, t - od, false),
  ];
}

/**
 * KAVELALI PANO (dowel) — pano + bir kenar boyunca eşit aralıklı silindir delikler.
 * Delikler boy (Y) boyunca dizilir, X=konum hattında, üstten girer (derinlik kadar iner).
 */
export function kavelaliPanoMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 600, boy = +p['boy'] || 400, t = +p['kalinlik'] || 18;
  const adet = Math.max(1, Math.round(+p['adet'] || 4));
  const cap = +p['cap'] || 8;
  const derin = Math.min(t - 2, +p['derinlik'] || 12);
  const ox = (typeof p['konum'] === 'number') ? +p['konum'] : en / 2;
  const kenarPay = +p['kenarPay'] || 40;

  const out: IOzellik[] = [kutuMerkez(en, boy, t, en / 2, boy / 2, 0, true)];
  const span = Math.max(1, boy - 2 * kenarPay);
  for (let i = 0; i < adet; i++) {
    const yy = adet === 1 ? boy / 2 : kenarPay + (span * i) / (adet - 1);
    // delik üstten girer: taban kotu (t-derin), derin+1 yukarı taşar
    out.push(silindirFeat(cap, derin + 1, xyDuzlem(ox, yy, t - derin), false));
  }
  return out;
}

/**
 * KAPAK (frame & panel) — 4 çevre çıta + ortada ince ayna paneli.
 * Lokal: en=X (genişlik), boy=Y (yükseklik), t=Z (derinlik/kalınlık).
 */
export function kapakMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 400, boy = +p['boy'] || 600, t = +p['kalinlik'] || 20;
  const cita = +p['cita'] || 70;                       // çıta genişliği
  const aynaT = Math.max(4, +p['aynaKalinlik'] || 8);  // ayna paneli kalınlığı
  const orta = boy - 2 * cita;

  const out: IOzellik[] = [
    kutuMerkez(en, cita, t, en / 2, cita / 2, 0, true),               // alt çıta
    kutuMerkez(en, cita, t, en / 2, boy - cita / 2, 0, true),         // üst çıta
    kutuMerkez(cita, orta, t, cita / 2, boy / 2, 0, true),           // sol çıta
    kutuMerkez(cita, orta, t, en - cita / 2, boy / 2, 0, true),      // sağ çıta
  ];
  const ai = en - 2 * cita + 16, aj = boy - 2 * cita + 16;            // çıta içine 8mm girer
  if (ai > 1 && aj > 1) out.push(kutuMerkez(ai, aj, aynaT, en / 2, boy / 2, 0, true));
  return out;
}

/**
 * ÇEKMECE — basit çekmece kutusu: ön+arka+sol+sağ yan + içe oturan taban (üstü açık).
 */
export function cekmeceMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 500, boy = +p['boy'] || 400, yuk = +p['yuk'] || 120;
  const t = +p['kalinlik'] || 15;
  const tt = +p['tabanKalinlik'] || 6;
  const icBoy = boy - 2 * t;
  return [
    kutuMerkez(en, t, yuk, en / 2, t / 2, 0, true),                  // ön
    kutuMerkez(en, t, yuk, en / 2, boy - t / 2, 0, true),           // arka
    kutuMerkez(t, icBoy, yuk, t / 2, boy / 2, 0, true),             // sol
    kutuMerkez(t, icBoy, yuk, en - t / 2, boy / 2, 0, true),        // sağ
    kutuMerkez(en - 2 * t, icBoy, tt, en / 2, boy / 2, 0, true),    // taban (içe oturur)
  ];
}

/** Makro id → üreteç (FikoModellemeYonetici buradan çağırır; insaat ile aynı kayıt deseni). */
export const MAKRO_URETECLER_MARANGOZ: Record<string, (p: IMakroParam) => IOzellik[]> = {
  panel: panelMakro,
  karkas: karkasMakro,
  rafliDolap: rafliDolapMakro,
  kinisliPano: kinisliPanoMakro,
  kavelaliPano: kavelaliPanoMakro,
  kapak: kapakMakro,
  cekmece: cekmeceMakro,
};
